// D36 composer: the attachment upload (apps/api/src/routes/assistant-attachments.ts). The body is the file itself (no
// JSON, no base64), with the same cookie session, CSRF header and structured errors as `api`.
import type { AssistantAttachment } from "@maestros/contracts";
import { ApiRequestError, buildUrl, requestHeaders, toApiError } from "./client.ts";

/** 201 with the stored attachment; 413 above 10 MB; 422 for a refused type or content (details.cause). */
export async function uploadAssistantAttachment(file: Blob, name: string, projectId: string | null, signal?: AbortSignal): Promise<AssistantAttachment> {
  let response: Response;
  try {
    response = await fetch(buildUrl("/assistant/attachments", { name, project_id: projectId }), {
      method: "POST",
      credentials: "same-origin",
      headers: { ...requestHeaders("POST"), "content-type": file.type || "application/octet-stream" },
      body: file,
      signal: signal ?? null,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiRequestError({ status: 0, code: "network", message: "The upload was interrupted; try again.", retryable: true });
  }
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as AssistantAttachment;
}

/**
 * D36.4: the bytes of one of the caller's attachments (`GET /v1/assistant/attachments/:id/content`, owner-only), for a
 * thumbnail. The server answers SVG as application/octet-stream and every file as a download; the caller re-types the
 * blob with the stored image type and shows it only through an <img> blob URL (an image never runs script).
 */
export async function fetchAssistantAttachmentContent(id: string, signal?: AbortSignal): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(buildUrl(`/assistant/attachments/${encodeURIComponent(id)}/content`), { method: "GET", credentials: "same-origin", headers: requestHeaders("GET"), signal: signal ?? null });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiRequestError({ status: 0, code: "network", message: "The attachment could not be loaded.", retryable: true });
  }
  if (!response.ok) throw await toApiError(response);
  return response.blob();
}
